import { supabaseClient } from '@/services/supabaseClient';
import { databaseService } from './databaseService';
import { botService } from './botService';
import type { ChatMessage, UserProfile } from '@/types';
import { personaRewriteService } from './personaRewriteService';

type ChannelKind = 'world' | 'realm';

class GlobalChatService {
  private listeners = new Map<string, (msg: ChatMessage) => void>();
  private localBuffers = new Map<string, ChatMessage[]>();
  private activeChannels = new Map<string, any>(); // Track active channels to prevent duplicates
  private realmId: string | null = null;

  initRealm(realmId: string | null) {
    this.realmId = realmId;
  }

  subscribe(kind: ChannelKind, callback: (msg: ChatMessage) => void): () => void {
    const key = this.key(kind);
    this.listeners.set(key, callback);

    const channelName = this.channelName(kind);

    // Check if we already have an active channel for this kind
    if (this.activeChannels.has(channelName)) {
      console.log(`[GlobalChat] Reusing existing channel: ${channelName}`);
      // Flush any buffered messages to the new callback
      (this.localBuffers.get(key) || []).forEach(m => callback(m));

      // Return unsubscribe that only removes this listener
      return () => {
        this.listeners.delete(key);
      };
    }

    // Setup new Supabase realtime channel with error handling
    console.log(`[GlobalChat] Creating new channel: ${channelName}`);
    const ch = supabaseClient.channel(channelName, { config: { broadcast: { self: true } } });

    ch.on('broadcast', { event: 'message' }, (payload) => {
      const data = payload.payload as ChatMessage;
      this.pushLocal(key, data);
      callback(data);
    }).subscribe(async (status, err) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[GlobalChat] ✅ Subscribed to ${channelName}`);
        // History fetch from DB (best-effort)
        try {
          const history = await (kind === 'world'
            ? databaseService.getWorldChatHistory(50)
            : databaseService.getRealmChatHistory(this.realmId || 'default', 50));
          (history || []).forEach((m: ChatMessage) => this.pushLocal(key, m));
        } catch {}
        // Flush buffer to UI
        (this.localBuffers.get(key) || []).forEach(m => callback(m));
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error(`[GlobalChat] ❌ Channel error for ${channelName}:`, err);
        // Remove from active channels so it can be recreated
        this.activeChannels.delete(channelName);
      } else if (status === 'CLOSED') {
        console.log(`[GlobalChat] Channel closed: ${channelName}`);
        this.activeChannels.delete(channelName);
      }
    });

    this.activeChannels.set(channelName, ch);

    // Return unsubscribe
    return () => {
      this.listeners.delete(key);
      const channel = this.activeChannels.get(channelName);
      if (channel && this.listeners.size === 0) {
        // Only unsubscribe if this was the last listener
        console.log(`[GlobalChat] Unsubscribing from ${channelName}`);
        supabaseClient.removeChannel(channel);
        this.activeChannels.delete(channelName);
      }
    };
  }

  async send(kind: ChannelKind, inputText: string, profile: UserProfile | null | undefined): Promise<ChatMessage | null> {
    // Commands: /meet <bot-id>
    if (inputText.trim().toLowerCase().startsWith('/meet ')) {
      const botId = inputText.trim().slice(6).trim();
      try {
        await botService.engage(botId, this.realmId || 'default');
        await botService.maybeAct(botId, this.realmId || 'default');
      } catch {}
      // fallthrough to send visible notice
    }
    const rewritten = personaRewriteService.rewrite(inputText, profile, {
      nsfw18Plus: true,
      karmaContext: { recentHistory: [], playerLevel: 1, location: 'chat' }
    });
    if (!rewritten) return null;

    const message: ChatMessage = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      sender: profile?.displayName || profile?.name || 'Wanderer',
      userId: profile?.uid || profile?.id,
      userName: profile?.displayName || profile?.name,
      text: rewritten,
      timestamp: Date.now()
    };

    // Broadcast via Supabase realtime
    try {
      await supabaseClient.channel(this.channelName(kind)).send({
        type: 'broadcast', event: 'message', payload: message
      });
      // Best-effort persistence
      try {
        if (kind === 'world') await databaseService.saveWorldChatMessage(message);
        else await databaseService.saveRealmChatMessage(this.realmId || 'default', message);
      } catch {}
    } catch (e) {
      // Fallback to local buffer if realtime unavailable
      const key = this.key(kind);
      this.pushLocal(key, message);
      const cb = this.listeners.get(key);
      cb && cb(message);
    }

    return message;
  }

  getBuffer(kind: ChannelKind): ChatMessage[] {
    return [...(this.localBuffers.get(this.key(kind)) || [])];
  }

  private channelName(kind: ChannelKind): string {
    if (kind === 'world') return 'world:chat';
    const r = this.realmId || 'default';
    return `realm:chat:${r}`;
  }

  private key(kind: ChannelKind) { return `${kind}:${this.realmId || 'default'}`; }

  private pushLocal(key: string, msg: ChatMessage) {
    if (!this.localBuffers.has(key)) this.localBuffers.set(key, []);
    this.localBuffers.get(key)!.push(msg);
    // Keep last 200
    const arr = this.localBuffers.get(key)!;
    if (arr.length > 200) arr.shift();
  }

  // Optional: very simple topic bot stub
  async maybeBotReply(kind: ChannelKind, topic: string, seedMsg: ChatMessage) {
    // Placeholder for future bot integration; no-op for now
    return;
  }
}

export const globalChatService = new GlobalChatService();
